import React, { useState, forwardRef } from 'react';

import styled from '@emotion/styled';
import { colors, fonts } from '@modernanimal/common';

type Props = {
    // Native HTMLInput props
    value: string;
    onChange: (value: string)=> void;
    id?: string;
    placeholder?: string;
    autoFocus?: boolean;
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement> | undefined;
    maxLength?: number;
    onFocus?: React.FocusEventHandler<HTMLInputElement> | undefined;
    onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
    type?: React.HTMLInputTypeAttribute;
    inputMode?: 'email' | 'search' | 'tel' | 'text' | 'url' | 'none' | 'numeric' | 'decimal';
    dataTestId?: string;

    // Custom style props
    isInvalid?: boolean;
    isTextCentered?: boolean;
    hasRightIcon?: boolean;

    // Date input props
    min?: string;
    max?: string;
    disabled?: boolean;
};

const InputField = ({
    value, onChange, placeholder, autoFocus = false, onKeyDown, maxLength, isInvalid = false,
    onFocus, onBlur, type = 'text', inputMode, min, max, dataTestId, id, isTextCentered = false,
    hasRightIcon = false, disabled = false
}: Props, ref?: React.Ref<HTMLInputElement>) => {
    const [isFocused, setIsFocused] = useState(false);

    const isActive = isFocused || !!value;

    return (
        <InputContainer>
            <Input
                id={id}
                ref={ref}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onFocus={(e) => { setIsFocused(true); onFocus?.(e); }}
                onBlur={(e) => { setIsFocused(false); onBlur?.(e); }}
                type={type}
                inputMode={inputMode}
                autoFocus={autoFocus}
                onKeyDown={onKeyDown}
                maxLength={maxLength}
                min={min}
                max={max}
                data-testid={dataTestId}
                className={(type === 'date' && !isActive) ? 'date-input-placeholder' : undefined}
                isActive={isFocused || !!value}
                isInvalid={isInvalid}
                isTextCentered={isTextCentered}
                hasRightIcon={hasRightIcon}
                disabled={disabled}
            />
        </InputContainer>
    );
};

export default forwardRef(InputField);

const InputContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
`;

const Input = styled.input<{
    isActive: boolean,
    isInvalid: boolean,
    isTextCentered: boolean,
    hasRightIcon: boolean
}>`
    display: flex;
    background-color: transparent;
    padding: 0 12px;
    ${({ hasRightIcon }) => (hasRightIcon && 'padding-right: 10%;')}
    border-radius: 8px;
    height: 48px;
    width: 100%;
    background-color: ${({ isActive }) => (isActive ? colors.WHITE : colors.GREY_6)};
    border: ${({ isActive, isInvalid }) => {
        let borderColor = isActive ? colors.PERIWINKLE_4 : colors.GREY_6;
        if (isInvalid) borderColor = colors.RED;
        return `1px solid ${borderColor}`;
    }};
    text-overflow: ellipsis;

    ${({ isTextCentered }) => (isTextCentered && 'text-align: center;')}

    &:focus {
        outline: none!important;
    };

    ::placeholder, ::-webkit-input-placeholder {
        color: ${colors.GREY_3};
        font-family: ${fonts.base.fontFamily};
        font-size: 16px;
    };

    // Make native calendar icon white when using date input
    // so custom calendar icon can be used
    appearance: none; // Used for mobile date input to be 100% width
    &[type="date"]::-webkit-calendar-picker-indicator {
        filter: invert(1);
    }
    &[type="date"]::-webkit-appearance: none;
    &[type="date"].date-input-placeholder::-webkit-datetime-edit {
        color: ${colors.GREY_3};
    }
    &[type="date"]::-webkit-date-and-time-value {
        text-align: left;
    }
`;
